import clsx from "clsx";
import type { ComponentPropsWithoutRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faTriangleExclamation,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Badge } from "@/components/ui/badge/Badge";
import { CC_HEADER_META_CHIP } from "@/components/case-checking/caseCheckingChrome";
import {
  CASE_ROLE_LABELS,
  type CaseGrade,
  type CaseRole,
  type PeerReviewWhoIsRight,
  type RoleGrade,
} from "@/services/api/case-checking";

type PeerAgreement = "agree" | "partial" | "disagree";

/** Aligns with `Grade` in CaseCheckingView */
export type CaseCheckingGrade = "suitable" | "dev-needs" | "unclear" | "unsuitable" | "stop";

/** Shared palette for final-case grades (Summary, list, peer comparison). */
export const CASE_CHECKING_REVIEW_GRADE_BADGE: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  suitable: {
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    text: "text-emerald-700 dark:text-emerald-300",
    label: "Suitable",
  },
  "dev-needs": {
    bg: "bg-amber-100 dark:bg-amber-900/30",
    text: "text-amber-700 dark:text-amber-300",
    label: "Suitable with Dev",
  },
  unclear: {
    bg: "bg-orange-100 dark:bg-orange-900/30",
    text: "text-orange-700 dark:text-orange-300",
    label: "Unclear",
  },
  unsuitable: {
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-300",
    label: "Unsuitable",
  },
  stop: {
    bg: "bg-red-600 dark:bg-red-500/80",
    text: "text-white dark:text-white",
    label: "Stop",
  },
};

const GRADE_SUBLABEL: Record<string, string> = {
  suitable: "Pass",
  "dev-needs": "Pass",
  unclear: "Fail",
  unsuitable: "Fail",
  stop: "Fail",
};

/** List/kanban and header chips — muted text grades; avoid amber/orange (reserved for action cues elsewhere). */
const LIST_GRADE_TEXT: Record<string, string> = {
  suitable: "text-emerald-700 dark:text-emerald-400/85",
  "dev-needs": "text-sky-700 dark:text-sky-400/90",
  unclear: "text-slate-600 dark:text-slate-400",
  unsuitable: "text-rose-700 dark:text-rose-400/90",
  stop: "text-red-800 dark:text-red-300 font-bold",
};

/** Case Details / Summary — status column */
export function SummaryReviewGradePill({
  grade,
  size = "default",
}: {
  grade: string;
  size?: "default" | "compact";
}) {
  const g =
    CASE_CHECKING_REVIEW_GRADE_BADGE[grade] ??
    CASE_CHECKING_REVIEW_GRADE_BADGE.unclear;
  if (size === "compact") {
    return (
      <span
        className={clsx(
          CC_HEADER_META_CHIP,
          "font-semibold",
          LIST_GRADE_TEXT[grade] ?? LIST_GRADE_TEXT.unclear,
        )}
        data-testid="summary-review-grade-pill"
      >
        {g.label}
      </span>
    );
  }
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-semibold tracking-tight",
        g.bg,
        g.text,
      )}
    >
      {g.label}
    </span>
  );
}

const GRADE_PHRASE: Record<string, string> = {
  suitable: "suitable",
  "dev-needs": "suitable with development",
  unclear: "unclear",
  unsuitable: "unsuitable",
  stop: "stop",
};

const GRADE_HEADLINE_LABEL: Record<CaseCheckingGrade, string> = {
  suitable: "Suitable",
  "dev-needs": "Suitable with Dev",
  unclear: "Unclear",
  unsuitable: "Unsuitable",
  stop: "Stop",
};

/** When system roll-up and agent file grade diverge, explain in plain language. */
export function agentGradeOverrideNote(
  baseline: string | null | undefined,
  final: string | null | undefined,
): string | null {
  if (!baseline || !final || baseline === final) return null;
  const baselinePhrase = GRADE_PHRASE[baseline] ?? baseline.replace(/-/g, " ");
  const finalPhrase = GRADE_PHRASE[final] ?? final.replace(/-/g, " ");
  return `Grades roll up as '${baselinePhrase}' but the agent believed the advice is very likely to be '${finalPhrase}'.`;
}

/** Normalise API grade strings to the four case-check grades. */
export function normalizeCaseCheckingGrade(raw: string | null | undefined): CaseCheckingGrade | null {
  if (!raw) return null;
  const s = raw.toLowerCase().trim();
  if (s === "stop") return "stop";
  if (s.includes("dev")) return "dev-needs";
  if (s.includes("unclear")) return "unclear";
  if (s.includes("unsuit")) return "unsuitable";
  if (s.includes("suit")) return "suitable";
  return null;
}

/**
 * System roll-up before the agent's suitability judgment (Tab 8 baseline).
 * Pair with `caseReviewRollupGrade` + `agentGradeOverrideNote` when they diverge.
 */
export function caseReviewBaselineGrade(
  review: { baseline_grade?: CaseGrade | null; overall_grade?: CaseGrade | null } | null | undefined,
  fallback?: CaseGrade | string | null,
): CaseCheckingGrade {
  return (
    normalizeCaseCheckingGrade(review?.baseline_grade ?? review?.overall_grade ?? fallback ?? "unclear") ??
    "unclear"
  );
}

/**
 * Grade shown across case UI — agent `final_grade` when set, else system baseline.
 * When baseline and final differ, pair with `agentGradeOverrideNote`.
 */
export function caseReviewRollupGrade(
  review: {
    baseline_grade?: CaseGrade | null;
    final_grade?: CaseGrade | null;
    overall_grade?: CaseGrade | null;
  } | null | undefined,
  fallback?: CaseGrade | string | null,
): CaseCheckingGrade {
  return (
    normalizeCaseCheckingGrade(
      review?.final_grade ?? review?.baseline_grade ?? review?.overall_grade ?? fallback ?? "unclear",
    ) ?? "unclear"
  );
}

export function caseReviewGradeHeadline(grade: CaseCheckingGrade): string {
  return GRADE_HEADLINE_LABEL[grade] ?? CASE_CHECKING_REVIEW_GRADE_BADGE[grade]?.label ?? grade;
}

/** Large overview headline — uses short labels (e.g. SWD). */
export function CaseReviewHeadlineGrade({
  grade,
  className,
}: {
  grade: CaseCheckingGrade;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        "shrink-0 text-xl font-semibold tabular-nums tracking-tight",
        LIST_GRADE_TEXT[grade] ?? LIST_GRADE_TEXT.unclear,
        className,
      )}
      data-testid="case-review-headline-grade"
    >
      {caseReviewGradeHeadline(grade)}
    </span>
  );
}

export type HumanReviewRouting =
  | "no_formal_review"
  | "needs_human"
  | "priority_human";

const HUMAN_REVIEW_LABEL: Record<HumanReviewRouting, string> = {
  no_formal_review: "Routine case profile",
  needs_human: "Complex case flagged",
  priority_human: "High-risk case flagged",
};

const HUMAN_REVIEW_HELPER: Record<HumanReviewRouting, string> = {
  no_formal_review:
    "Evidence and outcome sit within normal expectations for this case type.",
  needs_human:
    "Material gaps or judgment calls mean this file warrants a closer compliance look.",
  priority_human:
    "Significant regulatory or client-detriment signals — prioritise review.",
};

export function humanReviewDisplayLabel(
  value: HumanReviewRouting | null | undefined,
): string | null {
  if (!value) return null;
  return HUMAN_REVIEW_LABEL[value] ?? null;
}

export function humanReviewHelperText(
  value: HumanReviewRouting | null | undefined,
): string | null {
  if (!value) return null;
  return HUMAN_REVIEW_HELPER[value] ?? null;
}

export type AdjudicatorGradeCall = "open" | "agree_agent" | "agree_human" | "partial";

const ADJUDICATOR_GRADE_CALL_LABEL: Record<AdjudicatorGradeCall, string> = {
  agree_agent: "Sides with agent grade",
  agree_human: "Sides with human grade",
  partial: "Partial agreement",
  open: "Still open",
};

const ADJUDICATOR_GRADE_CALL_HELPER: Record<AdjudicatorGradeCall, string> = {
  agree_agent: "The adjudicator agrees the agent's overall grade is the right call.",
  agree_human: "The adjudicator agrees the human reviewer's grade is the right call.",
  partial: "The adjudicator sees merit in both positions.",
  open: "The adjudicator has not settled on an overall grade call yet.",
};

export function adjudicatorGradeCallLabel(
  value: AdjudicatorGradeCall | string | null | undefined,
): string | null {
  if (!value) return null;
  return ADJUDICATOR_GRADE_CALL_LABEL[value as AdjudicatorGradeCall] ?? null;
}

export function adjudicatorGradeCallHelper(
  value: AdjudicatorGradeCall | string | null | undefined,
): string | null {
  if (!value) return null;
  return ADJUDICATOR_GRADE_CALL_HELPER[value as AdjudicatorGradeCall] ?? null;
}

function humanReviewDotClass(value: HumanReviewRouting | null | undefined): string {
  switch (value) {
    case "no_formal_review":
      return "bg-emerald-400";
    case "needs_human":
      return "bg-amber-400";
    case "priority_human":
      return "bg-rose-400";
    default:
      return "bg-slate-500";
  }
}

/** Human review routing — muted chip with green / amber / red status dot. */
export function HumanReviewStatusChip({
  value,
  className,
  size = "compact",
  ...rest
}: {
  value: HumanReviewRouting | null | undefined;
  className?: string;
  size?: "compact" | "panel";
} & ComponentPropsWithoutRef<"span">) {
  const label = humanReviewDisplayLabel(value);
  if (!label) return null;
  return (
    <span
      className={clsx(
        size === "panel"
          ? "inline-flex max-w-full items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1 text-sm font-normal leading-snug ring-1 ring-slate-200 dark:bg-white/[0.04] dark:ring-white/10 text-slate-600 dark:text-slate-400"
          : clsx(CC_HEADER_META_CHIP, "max-w-full gap-1.5 font-normal text-slate-600 dark:text-slate-400"),
        className,
      )}
      data-testid="human-review-status-chip"
      {...rest}
    >
      <span
        className={clsx("shrink-0 rounded-full", humanReviewDotClass(value), size === "panel" ? "h-2 w-2" : "h-1.5 w-1.5")}
        aria-hidden
      />
      <span className="truncate">{label}</span>
    </span>
  );
}

/** Passed / present / classified check icons — readable on dark UI */
export const ICON_CHECK_PASSED = "text-emerald-500 dark:text-emerald-400";

/** Document list — classified vs needs-attention status glyph. */
export function DocumentClassificationStatusBadge({
  valid,
  size = "md",
  className,
}: {
  valid: boolean;
  size?: "sm" | "md";
  className?: string;
}) {
  const icon = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <span
      className={clsx("inline-flex shrink-0 items-center justify-center", className)}
      aria-label={valid ? "Classified" : "Needs attention"}
      title={valid ? "Classified" : "Needs attention"}
    >
      <FontAwesomeIcon
        icon={valid ? faCircleCheck : faTriangleExclamation}
        className={clsx(icon, valid ? ICON_CHECK_PASSED : "text-amber-400")}
      />
    </span>
  );
}

/** Cases list / Kanban — grade label only (colour on text, no pill). */
export function CaseListGradeBadge({
  grade,
  className,
}: {
  grade: CaseCheckingGrade;
  className?: string;
}) {
  const g =
    CASE_CHECKING_REVIEW_GRADE_BADGE[grade] ??
    CASE_CHECKING_REVIEW_GRADE_BADGE.unclear;
  return (
    <span
      className={clsx(
        "shrink-0 text-xs font-semibold tabular-nums",
        LIST_GRADE_TEXT[grade] ?? LIST_GRADE_TEXT.unclear,
        className,
      )}
    >
      {g.label}
    </span>
  );
}

/** Peer review table — muted text grades (matches cases list; no filled pills). */
export function PeerComparisonGradeCell({ grade }: { grade: CaseGrade }) {
  if (!grade) {
    return <span className="text-sm text-slate-500">Not set</span>;
  }
  const g =
    CASE_CHECKING_REVIEW_GRADE_BADGE[grade] ??
    CASE_CHECKING_REVIEW_GRADE_BADGE.unclear;
  const sub = GRADE_SUBLABEL[grade] ?? "";
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span
        className={clsx(
          "text-sm font-medium",
          LIST_GRADE_TEXT[grade] ?? LIST_GRADE_TEXT.unclear,
        )}
      >
        {g.label}
      </span>
      {sub ? <span className="text-[11px] text-slate-500">{sub}</span> : null}
    </span>
  );
}

/** Detected case type — same pill language as status (indigo when known). */
export function CaseTypeDisplayPill({
  label,
  detected,
}: {
  label: string;
  detected: boolean;
}) {
  return (
    <span
      className={clsx(
        "inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-semibold tracking-tight",
        detected
          ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300"
          : "bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400",
      )}
    >
      {label}
    </span>
  );
}

/** Kanban / small tags — tighter padding */
export function CaseTypeTagBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center max-w-full truncate text-[10px] font-semibold rounded-full px-2.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200">
      {label}
    </span>
  );
}

const PEER_AGREEMENT: Record<
  PeerAgreement,
  { label: string; className: string }
> = {
  agree: {
    label: "Assessors agree",
    className:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200",
  },
  partial: {
    label: "Partial agreement",
    className:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",
  },
  disagree: {
    label: "Assessors disagree",
    className: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200",
  },
};

export function PeerAssessorAgreementPill({
  agreement,
}: {
  agreement: PeerAgreement;
}) {
  const a = PEER_AGREEMENT[agreement];
  return (
    <span
      className={clsx(
        "inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold tracking-tight",
        a.className,
      )}
    >
      {a.label}
    </span>
  );
}

const PEER_AGREEMENT_TEXT: Record<PeerAgreement, { label: string; className: string }> = {
  agree: { label: "Agree", className: "text-emerald-700 dark:text-emerald-400/90" },
  partial: { label: "Partial", className: "text-amber-700 dark:text-amber-400/90" },
  disagree: { label: "Disagree", className: "text-rose-700 dark:text-rose-400/90" },
};

/** Peer review table — agreement as coloured text (no pill background). */
export function PeerAssessorAgreementText({
  agreement,
}: {
  agreement: PeerAgreement;
}) {
  const a = PEER_AGREEMENT_TEXT[agreement];
  return <span className={clsx("text-xs font-medium", a.className)}>{a.label}</span>;
}

const WHO_IS_RIGHT: Record<
  PeerReviewWhoIsRight,
  { label: string; bg: string; text: string }
> = {
  agent: {
    label: "Agent correct",
    bg: "bg-indigo-100 dark:bg-indigo-900/30",
    text: "text-indigo-800 dark:text-indigo-200",
  },
  human: {
    label: "Human correct",
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    text: "text-emerald-800 dark:text-emerald-200",
  },
  both: {
    label: "Both have merit",
    bg: "bg-amber-100 dark:bg-amber-900/30",
    text: "text-amber-800 dark:text-amber-200",
  },
  unclear: {
    label: "Unclear",
    bg: "bg-slate-100 dark:bg-slate-700",
    text: "text-slate-700 dark:text-slate-300",
  },
};

export function PeerWhoIsRightBadge({ who }: { who: PeerReviewWhoIsRight }) {
  const w = WHO_IS_RIGHT[who];
  return (
    <span
      className={clsx(
        "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold tracking-tight",
        w.bg,
        w.text,
      )}
    >
      {w.label}
    </span>
  );
}

/** Inline lean label for divergence rows (text only). */
export function PeerWhoIsRightLabel({ who }: { who: PeerReviewWhoIsRight }) {
  const w = WHO_IS_RIGHT[who];
  return <span className={clsx("text-xs font-medium", w.text)}>{w.label}</span>;
}

/** Tiny coloured dot for case-list role-grade strip. */
const ROLE_DOT_COLOR: Record<CaseGrade & string, string> = {
  suitable: "bg-emerald-500",
  "dev-needs": "bg-sky-500",
  unclear: "bg-slate-400",
  unsuitable: "bg-rose-500",
  stop: "bg-red-700",
};

export function RoleGradeDots({ roleGrades }: { roleGrades: RoleGrade[] }) {
  if (roleGrades.length === 0) return null;
  return (
    <div className="flex items-center gap-1" title="Per-role grades (Planner / Paraplanner / CE)">
      {roleGrades.map((rg) => (
        <span
          key={rg.role}
          aria-label={`${CASE_ROLE_LABELS[rg.role]}: ${rg.grade}`}
          title={`${CASE_ROLE_LABELS[rg.role]}: ${rg.grade}`}
          className={clsx(
            "inline-block h-1.5 w-1.5 rounded-full",
            ROLE_DOT_COLOR[rg.grade] ?? "bg-slate-400",
          )}
        />
      ))}
    </div>
  );
}

const ROLE_GRADE_PILL_LABEL: Record<CaseRole, string> = {
  planner: "Planner",
  paraplanner: "Paraplanner",
  ce: "CE",
};

/** Compact one-line per-role grade pill used in the Summary 3-up strip. */
export function RoleGradePill({
  role,
  grade,
}: {
  role: CaseRole;
  grade: Exclude<CaseGrade, null>;
}) {
  const palette =
    CASE_CHECKING_REVIEW_GRADE_BADGE[grade] ?? CASE_CHECKING_REVIEW_GRADE_BADGE.unclear;
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {ROLE_GRADE_PILL_LABEL[role]}
      </span>
      <span
        className={clsx(
          "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold tracking-tight",
          palette.bg,
          palette.text,
        )}
      >
        {palette.label}
      </span>
    </div>
  );
}

/** Rule id / slug — app `Badge` zinc sm; overrides mono uppercase for readable rule tokens. */
export function PeerRuleReferenceBadge({ rule }: { rule: string | null }) {
  if (!rule?.trim()) {
    return <span className="opacity-50">—</span>;
  }
  return (
    <Badge
      color="zinc"
      size="sm"
      title={rule}
      className="max-w-[220px] truncate !font-sans !normal-case !tracking-normal"
    >
      {rule}
    </Badge>
  );
}
